'use client';

import Image from 'next/image';
import { ArrowRight } from 'lucide-react';

import { Link } from '@/core/i18n/navigation';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/components/ui/button';

interface PhotoEffect {
  id: string;
  name: string;
  description: string;
  preview: string;
  url: string;
}

interface PhotoEffectsShowcaseProps {
  effects: PhotoEffect[];
  title?: string;
  description?: string;
  maxItems?: number;
  className?: string;
}

export function PhotoEffectsShowcase({
  effects,
  title,
  description,
  maxItems = 6,
  className,
}: PhotoEffectsShowcaseProps) {
  const displayedEffects = effects.slice(0, maxItems);

  return (
    <section className={cn('py-16 md:py-24', className)}>
      <div className="container">
        <div className="mx-auto max-w-6xl">
          {(title || description) && (
            <div className="mb-12 text-center">
              {title && (
                <h2 className="text-3xl font-bold sm:text-4xl">{title}</h2>
              )}
              {description && (
                <p className="text-muted-foreground mt-2 text-lg">
                  {description}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {displayedEffects.map((effect) => (
              <Link
                key={effect.id}
                href={effect.url}
                className="group relative overflow-hidden rounded-xl border bg-card transition-all hover:shadow-xl hover:border-primary"
              >
                <div className="aspect-square overflow-hidden bg-muted">
                  <Image
                    src={effect.preview}
                    alt={effect.name}
                    width={400}
                    height={400}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="mb-2 text-xl font-semibold">{effect.name}</h3>
                  <p className="text-muted-foreground text-sm">
                    {effect.description}
                  </p>
                </div>
                <div className="absolute top-4 right-4 rounded-full bg-primary/90 px-3 py-1 text-xs font-medium text-primary-foreground opacity-0 transition-opacity group-hover:opacity-100">
                  Try Now
                </div>
              </Link>
            ))}
          </div>

          {effects.length > maxItems && (
            <div className="mt-10 text-center">
              <Button asChild size="lg" variant="outline">
                <Link href="/ai-photo-effects-library" prefetch={false}>
                  View All {effects.length} Effects
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

